import { forwardRef, ReactNode, Ref, FC } from 'react'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import type { IconComponentType } from '../Icon/index.js'
import { Icon } from '../Icon/index.js'
import { ExclamationCircleIcon } from '../icons/index.js'
import { useInputColors } from '../utils/styles/index.js'

export type InputProps = React.ComponentProps<'input'> & {
  invalid?: boolean
  leadingIcon?: IconComponentType
  trailingIcon?: IconComponentType
  leadingAddon?: string | ReactNode
  trailingAddon?: string | ReactNode
  wrapperClassName?: React.ComponentProps<'input'>['className']
}
/**
 * The Input component is a component that is used to get user input in a text field.
 */
export const Input: FC<InputProps> = forwardRef(
  (props, ref: Ref<HTMLInputElement>) => {
    const {
      invalid = false,
      hidden,
      disabled,
      leadingIcon,
      trailingIcon,
      leadingAddon,
      trailingAddon,
      className,
      wrapperClassName,
      ...rest
    } = props
    const { inputClsx, iconClsx, backgroundClsx } = useInputColors({
      invalid,
      disabled,
    })

    return (
      <div
        className={clsx(
          'flex relative rounded-md shadow-sm isolate w-full',
          hidden && 'hidden',
          wrapperClassName,
        )}
      >
        <div className="relative flex items-stretch flex-grow focus-within:z-10">
          {leadingIcon && (
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <Icon className={clsx('h-5 w-5', iconClsx)}>{leadingIcon}</Icon>
            </div>
          )}
          {leadingAddon && (
            <span
              className={clsx(
                'inline-flex items-center px-3 rounded-s-md border border-e-0 border-line',
                backgroundClsx,
              )}
            >
              {leadingAddon}
            </span>
          )}

          <input
            ref={ref}
            className={twMerge(
              clsx(
                'block w-full rounded-md',
                'px-3 py-2',
                inputClsx,
                invalid && 'pe-10',
                leadingIcon && 'ps-10',
                trailingIcon && 'pe-10 ',
                leadingAddon && 'flex-1 min-w-0 block w-full rounded-s-none',
                leadingAddon && !trailingAddon && 'rounded-e-md',
                trailingAddon && 'flex-1 min-w-0 block w-full rounded-e-none',
                trailingAddon && !leadingAddon && 'rounded-s-md',
                className,
              ),
            )}
            type="text"
            disabled={disabled}
            hidden={hidden}
            {...rest}
          />

          {invalid && (
            <div className="absolute inset-y-0 end-0 pe-3 flex items-center pointer-events-none z-10">
              <ExclamationCircleIcon
                className="h-5 w-5 text-action-destructive"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {trailingIcon && (
          <div className="absolute inset-y-0 end-0 pe-3 flex items-center pointer-events-none z-10">
            <Icon className={clsx('h-5 w-5', iconClsx)}>{trailingIcon}</Icon>
          </div>
        )}
        {trailingAddon && (
          <span
            className={clsx(
              'inline-flex items-center px-3 rounded-e-md border border-s-0 border-line',
              backgroundClsx,
            )}
          >
            {trailingAddon}
          </span>
        )}
      </div>
    )
  },
)
